import {
  Avatar,
  cn,
  Divider,
  Popover,
  PopoverProps,
  Text,
  TruncatedContent,
  useBoolean,
} from "@aurem/ui";
import { useActiveProductAssignment } from "hooks/employee";
import { useBasePath } from "hooks/useBasePath";
import { ChevronsUpDownIcon } from "lucide-react";
import { useEmployment } from "providers/employee/EmploymentProvider";
import { Link } from "react-router-dom";

type Props = {
  className?: string;
  compact?: boolean;
  popoverProps?: Partial<
    Omit<PopoverProps, "open" | "onOpenChange" | "content" | "children">
  >;
};

export const ProductAssignmentSelector = ({
  className,
  compact,
  popoverProps,
}: Props) => {
  const { path } = useBasePath();
  const { employmentId, employment } = useEmployment();
  const { currentAssignment } = useActiveProductAssignment();
  const productAssignments = employment?.productAssignments ?? [];

  const { value: isOpen, setValue: setIsOpen } = useBoolean(false);

  const activeProductName = currentAssignment?.productInstance?.name;

  if (productAssignments.length === 0) {
    return null;
  }

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      content={
        <div className="flex flex-col p-4">
          {productAssignments.map((productAssignment, index) => (
            <div key={productAssignment.id} className="flex flex-col">
              <Link
                to={path(
                  `/employment/${employmentId}/${productAssignment.id}/dashboard`,
                )}
                className="hover:bg-neutral-500/10 rounded-md p-2"
                onClick={() => setIsOpen(false)}
              >
                <Text size="md">{productAssignment.productInstance.name}</Text>
              </Link>
              {index < productAssignments.length - 1 && (
                <Divider direction="horizontal" className="my-2" />
              )}
            </div>
          ))}
        </div>
      }
      placement="right-end"
      contentClassName="p-0"
      offset={{
        mainAxis: 8,
        crossAxis: 8,
      }}
      {...popoverProps}
    >
      {compact ? (
        <div
          className={cn(
            "flex items-center justify-center cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-current",
            className,
          )}
        >
          <Avatar text={activeProductName ?? ""} size="md" shape="round" />
        </div>
      ) : (
        <div
          className={cn(
            "flex items-center gap-2 w-full min-w-0 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-current",
            className,
          )}
        >
          <TruncatedContent
            className="w-full text-start font-medium"
            as="span"
            maxLines={1}
          >
            {activeProductName}
          </TruncatedContent>
          <ChevronsUpDownIcon size="1rem" className="shrink-0" />
        </div>
      )}
    </Popover>
  );
};
